import { StatsigContext, useStatsigClient } from '@statsig/react-bindings'
import stableStringify from 'fast-json-stable-stringify'
import { useEffect, useMemo, useState, useContext } from 'react'

import { SortSelectContext } from 'components/'
import { useAttribution } from 'components/app/AttributionContext'
import { useClientGeoLocation } from 'components/app/clientGeoLocationContext'
import { fetchCardsSlugComplex } from './fetch-cards-slug-complex'
import { fetchCardsSlugSimple } from './fetch-cards-slug-simple'
import {
  EMPTY_RETURN,
  getValidSlugs,
  type FetchCardsSlugReturn,
  type SortData,
  type UseFetchCardsSlugCallback,
} from './use-fetch-cards-helper'

export function useFetchCardsSlug(
  possibleSlugs?: string[] | unknown,
  sortData?: SortData,
  callback?: UseFetchCardsSlugCallback,
): FetchCardsSlugReturn {
  const validSlugs = useMemo(
    () => getValidSlugs(possibleSlugs),
    [possibleSlugs],
  )
  const [result, setResult] = useState<FetchCardsSlugReturn>(EMPTY_RETURN)
  const {
    client: { loadingStatus },
  } = useContext(StatsigContext)
  const { attribution } = useAttribution()
  const clientGeoLocation = useClientGeoLocation()
  const { getDynamicConfig, getExperiment } = useStatsigClient()
  const { sort, weights, adjustment } = useContext(SortSelectContext)
  const slugHash = useMemo(() => stableStringify(validSlugs), [validSlugs])
  const weightsHash = useMemo(() => stableStringify(weights), [weights])

  useEffect(() => {
    if (!validSlugs) {
      setResult(EMPTY_RETURN)
      return
    }

    let isMounted = true
    async function fetchCards(slugs: [string] & string[]) {
      setResult((prev) => ({
        ...prev,
        isFetching: true,
      }))
      const shouldUseComplexFecth =
        slugs.length > 1 && sortData && sort && loadingStatus === 'Ready'

      if (shouldUseComplexFecth) {
        await fetchCardsSlugComplex({
          slugs,
          sortData,
          sort,
          adjustment,
          attribution,
          clientGeoLocation,
          getDynamicConfig,
          getExperiment,
          callback: (result) => {
            if (isMounted) setResult(result)
          },
        })
      } else {
        await fetchCardsSlugSimple(slugs, (result) => {
          if (isMounted) setResult(result)
        })
      }
    }

    fetchCards(validSlugs)

    return () => {
      isMounted = false
      setResult((prev) => ({
        ...prev,
        isFetching: false,
      }))
    }
  }, [slugHash, weightsHash, loadingStatus, sort])

  return {
    ...result,
    cards: callback ? callback(result.cards, sort) : result.cards,
  }
}
